import type { ReactNode } from 'react';
import { Button, TableContainer, Text } from '@ksyos/design-system';
import { v4 as uuidv4 } from 'uuid';
import type {
    Episodes,
    JournalEntries,
} from '@frontend/components/pages/customControls/utils/v1/episodeJournals/episodeJournals';
import type { ValidationErrors } from '@frontend/utils';
import { getLanguage } from '@common/translate/translate';
import { EpisodeRecord } from '@frontend/components/pages/customControls/utils/v1/episodeJournals/episodeJournals';
import { EpisodesEntryAddEdit } from '@frontend/components/pages/customControls/utils/v1/episodeJournals/episodesEntryAddEdit';
import { ProfessionalSummaryDataAction } from '@frontend/components/pages/professionalSummaryData/ProfessionalSummaryDataSection';

type EpisodeJournalsCanAddRenderProps = {
    addEmptyEpisodeJournals: () => void;
    episodes: Episodes | undefined;
    errors?: ValidationErrors;
    journalEntries: JournalEntries;
    renderJournalEntries: () => ReactNode;
    selectedEpisodesCount: number;
    setEpisodes: React.Dispatch<React.SetStateAction<Episodes | undefined>>;
    setJournalEntries: React.Dispatch<React.SetStateAction<JournalEntries>>;
    setValidationKeys: React.Dispatch<React.SetStateAction<string[]>>;
    validationKeys: string[];
};
export function EpisodeJournalsCanAdd(props: Readonly<EpisodeJournalsCanAddRenderProps>) {
    const {
        addEmptyEpisodeJournals,
        episodes,
        errors,
        journalEntries,
        renderJournalEntries,
        selectedEpisodesCount,
        setEpisodes,
        setJournalEntries,
        validationKeys,
        setValidationKeys,
    } = props;
    const translate = getLanguage();

    return (
        <>
            <Text
                fontWeight="medium"
                size="sm"
                sx={{ mb: 2, mt: 4 }}
            >
                {translate.professionalSummary.episodesAndJournalsRead}
            </Text>
            {episodes && episodes?.size > 0 ? <TableContainer>{renderAddEpisodes()}</TableContainer> : null}
            <Button
                iconLeft="add"
                onClick={addEmptyEpisode}
                sx={{ mr: 5 }}
                variant="link"
            >
                Voeg extra {translate.professionalSummary.episode} toe
            </Button>

            {selectedEpisodesCount > 0 ? renderJournalEntries() : null}

            {selectedEpisodesCount > 0 && selectedEpisodesCount > (journalEntries?.size || 0) ? (
                <Button
                    iconLeft="add"
                    onClick={addEmptyEpisodeJournals}
                    variant="link"
                >
                    Voeg extra journaalregel episode toe
                </Button>
            ) : null}
        </>
    );

    function renderAddEpisodes() {
        if (!episodes) {
            return;
        }

        const episodesRows = [];
        let counter = 0;

        for (const [key, episode] of episodes.entrySeq().toArray() as [string, EpisodeRecord][]) {
            episodesRows.push(
                <EpisodesEntryAddEdit
                    episodesEntry={episode}
                    episodesEntryId={key}
                    errorKey={counter++}
                    errors={errors}
                    key={key}
                    onChange={handleChangeEpisodes}
                    onDelete={handleDeleteEpisodes}
                />
            );
        }

        return episodesRows;
    }

    function addEmptyEpisode() {
        setEpisodes(
            episodes?.set(
                uuidv4(),
                new EpisodeRecord({
                    action: ProfessionalSummaryDataAction.pick,

                    comment: '',
                    endDate: null,
                    id: uuidv4(),
                    startDate: null,
                    statusCode: '',
                    valueDisplayName: '',
                })
            )
        );
    }

    async function handleChangeEpisodes(Episode: EpisodeRecord, episodeEntryKey: string, keysToBeValidated: string[]) {
        setEpisodes(episodes?.set(episodeEntryKey, Episode));

        if (keysToBeValidated.length > 0 && !keysToBeValidated.some((key) => validationKeys.includes(key))) {
            setValidationKeys([...validationKeys, ...keysToBeValidated]);
        }
    }

    async function handleDeleteEpisodes(episodeEntryKey: string, episodeEntryId: string) {
        setEpisodes(episodes?.delete(episodeEntryKey));
        setJournalEntries(journalEntries.delete(episodeEntryId));
    }
}
